import React, {
  useCallback, useMemo, useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { ErrorHandler, InfiniteScrollComponent, usePaginatedQuery } from '@playbookux/playbook-web-core';
import { isEmpty } from 'lodash';
import withDashboardSidebar from '../../components/with-dashboard-sidebar';
import * as S from './styles';
import {
  COMPANIES_LIMIT, DEBOUNCE_TIME, GET_ALL_COMPANIES, ORDER_COMPANIES_BY,
} from '../pricing/graphql';
import useDebounce from '../../../core/hooks/use-debounce';
import { GET_LIST_RATING, GET_PROJECTS, LIMIT } from './graphql';
import EditNumberDialog from './components/edit-number-dialog';
import Table from './components/table';

const PROJECTS_LIMIT = 50;
const ORDER_PROJECTS_BY = 'name ASC';

function ManageAudience() {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [projectIdSelected, setProjectIdSelected] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [selectScreenerVersion, setSelectScreenerVersion] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [audienceChange, setAudienceChange] = useState({ id: '', ratingPercentage: '', typeUpdate: '' });
  const [segmentIdSelected, setSegmentIdSelected] = useState('');

  const [debouncedCompanySearch] = useDebounce(() => companySearch, DEBOUNCE_TIME, [companySearch]);
  const [debouncedProjectSearch] = useDebounce(() => projectSearch, DEBOUNCE_TIME, [projectSearch]);

  const { data: { companies = [] } = {}, loading } = useQuery(GET_ALL_COMPANIES, {
    variables: {
      limit: COMPANIES_LIMIT,
      offset: 0,
      order: ORDER_COMPANIES_BY,
      search: debouncedCompanySearch,
    },
  });

  const { data: { projects = [] } = {}, loading: projectLoading } = useQuery(GET_PROJECTS, {
    variables: {
      limit: PROJECTS_LIMIT,
      offset: 0,
      order: ORDER_PROJECTS_BY,
      ...(projectIdSelected ? { id: projectIdSelected } : { name: debouncedProjectSearch }),
    },
  });

  const workspaces = useMemo(() => companies?.map((company) => ({
    key: company.id,
    value: company.id,
    text: company.name,
  })), [companies]);

  const projectsOptions = useMemo(() => projects?.flatMap((project) => project.segments.map((segment) => ({
    key: project.id,
    value: `${project.id} ${segment.id}`,
    text: `${project.name} - ${segment.name}`,
  }))), [projects]);

  const screenerVersionOptions = useMemo(() => projects?.flatMap((project) => project.surveysVersions?.map((survey) => ({
    key: `${project.id}-${survey.version}`,
    value: `${survey.version.toString()} ${project.id}`,
    text: `${project.name} - Screener Version ${survey.version}`,
  }))), [projects]);

  const attributes = {
    variables: {
      limit: LIMIT,
      offset: 0,
      companyId: selectedCompany,
      projectId: projectIdSelected || '',
      screenerVersion: selectScreenerVersion,
      questionOrder: 'asc',
      segmentId: segmentIdSelected || '',
    },
    fetchPolicy: 'network-only',
  };

  const {
    data: { manageAudience = [] }, loading: loadingList, fetchMore, hasMore, error, refetch,
  } = usePaginatedQuery(GET_LIST_RATING, attributes, { dataField: 'manageAudience' });

  const handleQuestionOptionType = useCallback((disqualify, qualify) => {
    if (disqualify) {
      return '(Disqualify)';
    }

    if (qualify) {
      return '(Qualify)';
    }

    return '(May Select)';
  }, []);

  const handleOpenModalAndSetAudienceChange = useCallback((id, rate, typeUpdate) => {
    setAudienceChange({ id, ratingPercentage: rate, typeUpdate });
    setOpenModal(true);
  }, []);

  const handleCloseModalAndSetAudienceChange = () => {
    setOpenModal(false);
    setAudienceChange({ id: '', ratingPercentage: '', typeUpdate: '' });
  };

  const onRefetch = async () => {
    await refetch({
      limit: LIMIT,
      offset: 0,
      companyId: selectedCompany,
      projectId: projectIdSelected,
      screenerVersion: selectScreenerVersion,
    });
  };

  const renderTableRow = useCallback(({ questions }) => (
    questions?.map((question, index) => {
      const { screenerRatingPercentage, label, id } = question || {};
      return (
        <Table
          key={id}
          questionIndex={index}
          label={label}
          id={id}
          screenerRatingPercentage={screenerRatingPercentage}
          question={question}
          handleOpenModalAndSetAudienceChange={handleOpenModalAndSetAudienceChange}
          handleQuestionOptionType={handleQuestionOptionType}
        />
      );
    })
  ), [handleOpenModalAndSetAudienceChange, handleQuestionOptionType]);

  const handleSelectScreenerVersion = (value) => {
    const [version, projectId] = value.split(' ');
    setSelectScreenerVersion(version);
    setProjectIdSelected(projectId);
  };

  const handleSelectProject = (value) => {
    const [projectId, segmentId] = value.split(' ');
    setProjectIdSelected(projectId);
    setSegmentIdSelected(segmentId);
  };

  return (
    <>
      <S.Container>
        <S.Dropdown
          placeholder="Workspace name"
          fluid
          selection
          search
          options={workspaces}
          disabled={loading}
          loading={loading}
          onChange={(_, { value }) => setSelectedCompany(value)}
          onSearchChange={(_, { searchQuery }) => setCompanySearch(searchQuery)}
          clearable
        />
        <S.Dropdown
          placeholder="Project name"
          fluid
          selection
          search
          options={projectsOptions}
          disabled={projectLoading}
          loading={projectLoading}
          onChange={(_, { value }) => handleSelectProject(value)}
          onSearchChange={(_, { searchQuery }) => setProjectSearch(searchQuery)}
          clearable
          data-dropdown-project-name
        />
        <S.Dropdown
          placeholder="Screener Version"
          fluid
          selection
          search
          options={screenerVersionOptions}
          disabled={projectLoading}
          loading={projectLoading}
          onChange={(_, { value }) => handleSelectScreenerVersion(value)}
          clearable
        />
      </S.Container>

      <S.Container>
        {error && (
          <ErrorHandler
            error={error}
            defaultMessage="Error while retrieving manage audience."
          />
        )}

        {loadingList && (
          <S.Centered>
            <S.Loader size="big">Loading</S.Loader>
          </S.Centered>
        )}

        {isEmpty(manageAudience) && (
          <S.Centered>
            <S.Text>No Audience found</S.Text>
          </S.Centered>
        )}

        {(manageAudience?.length > 0 && !error) && (
          <S.Card fluid>
            <InfiniteScrollComponent
              hasMore={hasMore}
              onLoadMore={fetchMore}
              useWindow
            >
              <S.Table basic="very" columns={2}>
                {manageAudience?.map(renderTableRow)}
              </S.Table>
            </InfiniteScrollComponent>
          </S.Card>

        )}

        {openModal && (
          <EditNumberDialog
            open={openModal}
            onClose={handleCloseModalAndSetAudienceChange}
            audienceChange={audienceChange}
            onRefetch={onRefetch}
          />
        )}
      </S.Container>
    </>
  );
}

export default withDashboardSidebar(ManageAudience);
